import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { AlertCircle, QrCode, RotateCw } from "lucide-react";
import { fetchJson } from "@/lib/api";
import { setTenant } from "@/lib/analytics";
import { milkSurcharge as dishMilkSurcharge } from "@/lib/format";
import { tenantErrorMessage } from "@/lib/serverMessages";
import { TENANT } from "@/constants/testIds";

/**
 * TenantProvider — everything restaurant-specific for one `/r/:slug` storefront:
 * branding, tax/service rates, categories and the dish list, all fetched from the
 * API. Nothing about a restaurant is bundled with the app any more.
 *
 * Children only mount once the tenant has loaded, so screens can read
 * `useTenant()` synchronously without null checks.
 */
const TenantCtx = createContext(null);

export const TenantProvider = ({ children }) => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refetch = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const [restaurant, menu] = await Promise.all([
          fetchJson(`/restaurants/${encodeURIComponent(slug)}`),
          fetchJson(`/menu?r=${encodeURIComponent(slug)}`),
        ]);
        if (cancelled) return;
        // Before any screen renders, so every event from here on — ours and the
        // SDK's own page views — says which restaurant it happened in.
        setTenant(slug, (menu?.restaurant || restaurant)?.id);
        setData({
          restaurant: menu?.restaurant || restaurant,
          dishes: menu?.dishes || [],
          categories: menu?.categories || [],
          drinkSubcategories: menu?.drink_subcategories || [],
        });
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, reloadKey]);

  // Unavailable dishes stay in this map: order history and reorder must still be
  // able to resolve a name for something the kitchen has since stop-listed.
  const dishById = useMemo(() => {
    const m = new Map();
    (data?.dishes || []).forEach((d) => m.set(d.id, d));
    return m;
  }, [data]);

  const findDish = useCallback((id) => dishById.get(id), [dishById]);

  const restaurantName = data?.restaurant?.name;
  useEffect(() => {
    if (restaurantName) document.title = restaurantName;
  }, [restaurantName]);

  const value = useMemo(() => {
    const pricing = data?.restaurant?.pricing || {};
    return {
      slug,
      restaurant: data?.restaurant || null,
      dishes: data?.dishes || [],
      categories: data?.categories || [],
      drinkSubcategories: data?.drinkSubcategories || [],
      findDish,
      // Display-only: the server is the source of truth for what a guest pays.
      taxRate: Number(pricing.tax_rate ?? 0.1),
      serviceRate: Number(pricing.service_rate ?? 0.06),
      milkSurcharge: dishMilkSurcharge,
      loading,
      error,
      refetch,
    };
  }, [slug, data, findDish, loading, error, refetch]);

  if (loading) return <TenantLoading />;
  if (error) {
    return error.status === 404 ? (
      <TenantNotFound slug={slug} />
    ) : (
      <TenantError error={error} onRetry={refetch} />
    );
  }

  return <TenantCtx.Provider value={value}>{children}</TenantCtx.Provider>;
};

export const useTenant = () => {
  const ctx = useContext(TenantCtx);
  if (!ctx) throw new Error("useTenant must be inside TenantProvider");
  return ctx;
};

// ─── States shown instead of the storefront ────────────────────────────────

const Screen = ({ testId, children }) => (
  <div className="App">
    <div className="app-frame">
      <div
        data-testid={testId}
        className="min-h-screen px-8 flex flex-col items-center justify-center text-center"
      >
        {children}
      </div>
    </div>
  </div>
);

const TenantLoading = () => {
  const { t } = useTranslation();
  return (
    <Screen testId={TENANT.loading}>
      <div className="relative w-16 h-16 mb-5">
        <div className="absolute inset-0 rounded-full bg-[#E34A27]/10 animate-ping" />
        <div className="absolute inset-2 rounded-full bg-[#E34A27]" />
      </div>
      <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-neutral-500">Fuelly</div>
      <div className="text-lg font-black tracking-tight text-neutral-900 mt-1">
        {t("tenant.loading")}
      </div>
    </Screen>
  );
};

const TenantError = ({ error, onRetry }) => {
  const { t } = useTranslation();
  return (
    <Screen testId={TENANT.error}>
      <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mb-4">
        <AlertCircle className="text-red-500" />
      </div>
      <h1 className="text-xl font-black tracking-tight text-neutral-900">
        {t("tenant.errorTitle")}
      </h1>
      <p className="text-sm text-neutral-500 mt-2 max-w-[32ch]">{tenantErrorMessage(t, error)}</p>
      <button
        data-testid={TENANT.retryButton}
        onClick={onRetry}
        className="mt-6 h-12 px-6 rounded-2xl bg-neutral-900 text-white font-bold text-sm flex items-center gap-2 active:scale-[0.98] transition-transform"
      >
        <RotateCw size={15} /> {t("common.tryAgain")}
      </button>
    </Screen>
  );
};

const TenantNotFound = ({ slug }) => {
  const { t } = useTranslation();
  return (
    <Screen testId={TENANT.notFound}>
      <div className="w-14 h-14 rounded-full bg-neutral-100 flex items-center justify-center mb-4">
        <QrCode className="text-neutral-500" />
      </div>
      <h1 className="text-2xl font-black tracking-tight text-neutral-900">
        {t("tenant.notFoundTitle")}
      </h1>
      <p className="text-sm text-neutral-500 mt-2 max-w-[32ch]">
        <Trans
          i18nKey="tenant.notFoundBody"
          values={{ slug }}
          components={{ path: <span className="font-mono font-bold" /> }}
        />
      </p>
    </Screen>
  );
};

export default TenantProvider;
